import { useState } from 'react';
import { Icon } from '@iconify/react';
import { Workflow, Brain, Zap, Shield, Settings, Play, Code, Users } from 'lucide-react';
import IntegrationLandingBase from '@/components/integrations/IntegrationLandingBase';

export default function MakeLanding() {
  const [copiedCode, setCopiedCode] = useState(false);

  const handleCopySetupCode = async () => {
    const setupCode = `# 1. Connect Make.com to Memara
Add Memara module in Make.com

# 2. Create your first scenario
Set up triggers and actions for memory creation

# 3. Configure data mapping
Map trigger data to memory fields

# 4. Activate your scenarios
Start automated memory workflows`;
    try {
      await navigator.clipboard.writeText(setupCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = setupCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const handleGetStarted = () => {
    window.location.href = '/login';
  };

  const handleSeeDemo = () => {
    window.open('https://www.youtube.com/watch?v=ouW739Hx7v8', '_blank');
  };

  return (
    <IntegrationLandingBase
      registryIntegrationId="make_com"
      icon={
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-50 shadow" aria-label="Make.com Logo">
          <Icon icon="simple-icons:make" width={48} height={48} />
        </span>
      }
      title="Make.com Integration"
      description="Enterprise automation workflows with persistent memory infrastructure. Build sophisticated multi-step scenarios that automatically capture, process, and store contextual memories from complex data transformations and business processes."
      features={[
        { icon: <Workflow className="h-4 w-4 text-purple-600" />, title: 'Advanced Workflow Memory', description: 'Capture context from complex multi-step automation scenarios with intelligent data mapping.' },
        { icon: <Brain className="h-4 w-4 text-purple-600" />, title: 'Semantic Data Processing', description: 'AI-powered understanding of workflow data relationships and business context.' },
        { icon: <Zap className="h-4 w-4 text-purple-600" />, title: 'Real-time Memory Creation', description: 'Instant memory capture from workflow triggers with sub-second response times.' },
        { icon: <Shield className="h-4 w-4 text-purple-600" />, title: 'Enterprise-Grade Security', description: 'Multi-tenant memory isolation for business automation.' },
        { icon: <Code className="h-4 w-4 text-purple-600" />, title: 'API-First Architecture', description: 'RESTful memory operations with comprehensive webhook support and error handling.' },
        { icon: <Users className="h-4 w-4 text-purple-600" />, title: 'Team Collaboration', description: 'Shared workflow memory spaces with role-based access controls and audit trails.' },
      ]}
      setupSteps={[
        'Connect Make.com to Memara API endpoint',
        'Create your first automation scenario with memory actions',
        'Configure intelligent data mapping and memory schemas',
        'Activate scenarios for automated memory workflows',
      ]}
      ctas={[
        {
          label: '🚀 Get Started Free',
          onClick: handleGetStarted,
          className: 'bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 h-11 rounded-md text-base font-semibold',
        },
        {
          label: 'See Demo',
          onClick: handleSeeDemo,
          className: 'border-purple-200 text-purple-600 hover:bg-purple-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: <Play className="w-4 h-4 mr-2" />,
        },
        {
          label: copiedCode ? 'Copied!' : 'Copy Setup Code',
          onClick: handleCopySetupCode,
          className: 'border-purple-200 text-purple-600 hover:bg-purple-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: copiedCode ? <Brain className="w-4 h-4 mr-2" /> : <Settings className="w-4 h-4 mr-2" />,
        },
      ]}
      screenshots={[
        {
          id: 'make-scenario-builder',
          title: 'Scenario Builder',
          description: 'Create sophisticated automation workflows with Memara memory modules in Make.com',
          placeholder: 'Make.com scenario builder showing Memara memory creation and search modules',
          badge: 'Build'
        },
        {
          id: 'data-mapping',
          title: 'Intelligent Data Mapping',
          description: 'Map complex workflow data to contextual memories with AI-powered understanding',
          placeholder: 'Make.com data mapping interface showing trigger data to memory field connections',
          badge: 'Mapping'
        },
        {
          id: 'workflow-monitoring',
          title: 'Workflow Monitoring',
          description: 'Monitor memory creation workflows with comprehensive analytics and error handling',
          placeholder: 'Make.com dashboard showing Memara scenario execution logs and success metrics',
          badge: 'Monitor'
        }
      ]}
      videoUrl="https://www.youtube.com/watch?v=ouW739Hx7v8"
      microcopy="Trusted by automation engineers and enterprise development teams using Make.com for business process automation."
      trustedBy="Make.com, Enterprise Automation Teams, and more."
      testimonial="Memara's Make.com integration transforms our complex automation workflows into intelligent, context-aware business processes with persistent memory capabilities."
    />
  );
} 